export interface GridMetricsResult {
  totalPower: number // in MW
  totalLines: number // count of lines
  totalDistance: number // in km
  alerts: number // count of alerts
  voltageGroups: Record<
    number,
    {
      voltageLevel: number
      lineCount: number
      totalPower: number
      totalDistance: number
    }
  >
}

/**
 * Calculate grid metrics from project data
 * @param projects Array of projects with data
 * @returns Object containing calculated metrics
 */
export function calculateGridMetrics(projects: Array<any>): GridMetricsResult {
  // Default result with zero values
  const result: GridMetricsResult = {
    totalPower: 0,
    totalLines: 0,
    totalDistance: 0,
    alerts: 0,
    voltageGroups: {},
  }

  if (!projects || !projects.length) {
    return result
  }

  // Process data to group by voltage levels
  const voltageGroups = projects.reduce(
    (acc, project) => {
      try {
        if (!project.data?.Lines?.summary?.["Basic Information"]?.["Voltage"]?.value) {
          return acc
        }

        const voltage = project.data.Lines.summary["Basic Information"]["Voltage"].value
        const power = project.data.Lines.summary["Basic Information"]["Power Transfer"]?.value || 0
        const distance = project.data.Lines.summary["Basic Information"]["Line Details"]?.["Total Distance"]?.value || 0

        if (!acc[voltage]) {
          acc[voltage] = {
            voltageLevel: voltage,
            lineCount: 0,
            totalPower: 0,
            totalDistance: 0,
          }
        }

        acc[voltage].lineCount += 1
        acc[voltage].totalPower += power
        acc[voltage].totalDistance += distance

        return acc
      } catch (err) {
        console.error("Error processing project data:", err)
        return acc
      }
    },
    {} as Record<number, { voltageLevel: number; lineCount: number; totalPower: number; totalDistance: number }>,
  )

  // Calculate totals
  result.voltageGroups = voltageGroups
  result.totalPower = Object.values(voltageGroups).reduce((sum, group) => sum + group.totalPower, 0)
  result.totalLines = Object.values(voltageGroups).reduce((sum, group) => sum + group.lineCount, 0)
  result.totalDistance = Object.values(voltageGroups).reduce((sum, group) => sum + group.totalDistance, 0)

  // For now, alerts are hardcoded to 0, but this could be updated to check for actual alerts
  result.alerts = 0

  return result
}

